import { createContext, useCallback, useContext, useMemo, useRef, type JSX, type ReactNode } from "react";
import type { ComposerMode, ComposerDraftPatch } from "./useComposerDraft";

/** Description of the currently-mounted wrapper, registered for the apply handler. */
export interface MountedComposer {
  readonly mode: ComposerMode;
  readonly threadKey: string;
  /** Receives a revision-bearing patch from a `composer.apply` event. */
  applyOp(patch: ComposerDraftPatch): void;
}

interface MountRegistry {
  /** Currently-mounted composer or null. Single-slot per DEC-7. */
  current(): MountedComposer | null;
  register(m: MountedComposer): () => void;
}

const Ctx = createContext<MountRegistry | null>(null);

export function ComposerMountProvider({ children }: { children: ReactNode }): JSX.Element {
  const slotRef = useRef<MountedComposer | null>(null);

  const register = useCallback((m: MountedComposer): (() => void) => {
    slotRef.current = m;
    return (): void => {
      if (slotRef.current === m) slotRef.current = null;
    };
  }, []);

  const value = useMemo<MountRegistry>(
    () => ({
      current: (): MountedComposer | null => slotRef.current,
      register,
    }),
    [register],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useComposerMountRegistry(): MountRegistry {
  const ctx = useContext(Ctx);
  if (!ctx) {
    throw new Error(
      "useComposerMountRegistry must be used inside <ComposerMountProvider>",
    );
  }
  return ctx;
}
